<script setup lang="ts">
import { onMounted, ref } from 'vue';
import ChatView from './components/ChatView.vue';
import SettingsView from './components/SettingsView.vue';
import { beginSignIn, completeSignIn, isSignedIn, signOut } from './lib/auth';

type Phase = 'loading' | 'anonymous' | 'signed-in';
/** Two screens is not worth a router; this is the whole navigation model. */
type View = 'chat' | 'settings';

const phase = ref<Phase>('loading');
const view = ref<View>('chat');
const error = ref<string | null>(null);
const chatView = ref<InstanceType<typeof ChatView> | null>(null);

onMounted(async () => {
  try {
    await completeSignIn();
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : String(caught);
  }
  phase.value = isSignedIn() ? 'signed-in' : 'anonymous';
});

async function login(): Promise<void> {
  error.value = null;
  try {
    await beginSignIn();
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : String(caught);
  }
}

function logout(): void {
  signOut();
  phase.value = 'anonymous';
  view.value = 'chat';
}
</script>

<template>
  <div class="app">
    <header class="topbar">
      <div class="topbar__brand">
        <span class="topbar__mark" aria-hidden="true"></span>
        <span>Shipdesk</span>
      </div>
      <nav v-if="phase === 'signed-in'" class="topbar__actions">
        <button
          class="btn btn--small"
          type="button"
          @click="view = view === 'chat' ? 'settings' : 'chat'"
        >
          {{ view === 'chat' ? 'API key' : 'Terug naar chat' }}
        </button>
        <button class="btn btn--small" type="button" @click="logout">Uitloggen</button>
      </nav>
    </header>

    <p v-if="error" class="notice notice--error notice--floating">{{ error }}</p>

    <main v-if="phase === 'loading'" class="centered">
      <p class="muted">Laden…</p>
    </main>

    <main v-else-if="phase === 'anonymous'" class="centered">
      <div class="signin">
        <h1>Verzendlabels maken in gesprek</h1>
        <p>
          Log in en koppel je MyParcel API key. Daarna kun je in gewone taal bezorgopties opvragen,
          verzendlabels aanmaken en zendingen traceren.
        </p>
        <button class="btn btn--primary" type="button" @click="login">Inloggen</button>
      </div>
    </main>

    <template v-else>
      <!--
        ChatView stays mounted while settings are open: it owns the conversation,
        and unmounting it would throw the history away just because someone went
        to look at their API key. Settings is recreated each time so it re-reads
        the stored status.
      -->
      <ChatView
        ref="chatView"
        v-show="view === 'chat'"
        @open-settings="view = 'settings'"
      />
      <SettingsView v-if="view === 'settings'" @saved="chatView?.onApiKeySaved()" />
    </template>
  </div>
</template>

<style scoped>
.app {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  max-width: 900px;
  margin: 0 auto;
  background: var(--surface);
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
}

.topbar__actions {
  display: flex;
  gap: 8px;
}

.topbar__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 15px;
}

.topbar__mark {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: var(--accent);
}

.centered {
  flex: 1;
  display: grid;
  place-items: center;
  padding: 24px 16px;
}

.signin {
  max-width: 44ch;
  text-align: center;
}

.signin h1 {
  font-size: 22px;
  margin: 0 0 10px;
}

.signin p {
  color: var(--text-muted);
  font-size: 14px;
  line-height: 1.55;
  margin: 0 0 20px;
}

.notice--floating {
  margin: 12px 16px 0;
}
</style>
